import classNames from 'classnames';
import * as React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import subjectCardStyles from '../../SubjectCard/SubjectCard.module.css';
import type { BaseSubjectProps, StudyModelLayoutProps } from '../StudyModelLayout';
import baseStyles from '../StudyModelLayout.module.css';
import type { GridPosition } from '../Utils/layoutPositionUtils';
import {
    horizontalLayoutGridRowPositionForSemester,
    resolveHorizontalGridLayout,
    toCssGridPosition,
} from '../Utils/layoutPositionUtils';
import horizontalStyles from './StudyModelLayout.horizontal.module.css';

const renderEmptyMessage = (message: string) => (
    <span className={classNames(baseStyles.EmptyMessage, horizontalStyles.EmptyMessage)}>{message}</span>
);

const renderSubjects = <TSubjectProps extends BaseSubjectProps>(
    subjects: TSubjectProps[],
    gridColumnMapping: Record<string, GridPosition>,
    renderSubject: (subject: TSubjectProps) => ReactNode,
    gridRowResolver: (subject: TSubjectProps) => CSSProperties['gridRow']
) =>
    subjects.map(subject => (
        <div
            key={subject.code}
            className={horizontalStyles.Subject}
            style={{
                gridColumn: toCssGridPosition(gridColumnMapping[subject.code]),
                gridRow: gridRowResolver(subject),
            }}
        >
            {renderSubject(subject)}
        </div>
    ));

export const StudyModelHorizontalLayout = <TSubjectProps extends BaseSubjectProps>(
    props: StudyModelLayoutProps<TSubjectProps>
) => {
    const { subjects, firstHeading, secondHeading, renderSubject, noSubjectsMessage } = props;

    const useSingleRow = !secondHeading;
    const { columnsCount, gridColumnMapping } = React.useMemo(
        () => resolveHorizontalGridLayout(subjects, useSingleRow),
        [useSingleRow, subjects]
    );

    if (useSingleRow) {
        return (
            <StudyModelSingleRowHorizontalLayout
                columnsCount={columnsCount}
                firstHeading={firstHeading}
                subjects={subjects}
                renderSubject={renderSubject}
                noSubjectsMessage={noSubjectsMessage}
                gridColumnMapping={gridColumnMapping}
            />
        );
    }

    return (
        <div
            className={classNames(
                baseStyles.StudyModelLayout,
                horizontalStyles.StudyModelLayout,
                subjectCardStyles.ContainerWithStretchingCards
            )}
            style={{ gridTemplateColumns: `auto repeat(${columnsCount}, minmax(min-content, 1fr))` }}
        >
            <div className={classNames(baseStyles.SeparatorLine, horizontalStyles.SeparatorLine)} />
            <div
                className={classNames(baseStyles.Heading, horizontalStyles.Heading, horizontalStyles['Heading--first'])}
            >
                {firstHeading}
            </div>
            <div
                className={classNames(
                    baseStyles.Heading,
                    horizontalStyles.Heading,
                    horizontalStyles['Heading--second']
                )}
            >
                {secondHeading}
            </div>
            {subjects.length > 0
                ? renderSubjects(
                      subjects,
                      gridColumnMapping,
                      renderSubject,
                      subject => horizontalLayoutGridRowPositionForSemester[subject.semester]
                  )
                : renderEmptyMessage(noSubjectsMessage)}
            <div className={classNames(baseStyles.DashLine, horizontalStyles.DashLine)} />
        </div>
    );
};

interface StudyModelSingleRowHorizontalLayoutProps<TSubjectProps extends BaseSubjectProps>
    extends Pick<
        StudyModelLayoutProps<TSubjectProps>,
        'firstHeading' | 'subjects' | 'renderSubject' | 'noSubjectsMessage'
    > {
    columnsCount: number;
    gridColumnMapping: Record<string, GridPosition>;
}

export const StudyModelSingleRowHorizontalLayout = <TSubjectProps extends BaseSubjectProps>(
    props: StudyModelSingleRowHorizontalLayoutProps<TSubjectProps>
) => {
    const { columnsCount, firstHeading, subjects, renderSubject, gridColumnMapping, noSubjectsMessage } = props;

    return (
        <div
            className={classNames(
                baseStyles.StudyModelLayout,
                horizontalStyles.StudyModelLayout,
                horizontalStyles.SingleColumnLayout,
                subjectCardStyles.ContainerWithStretchingCards
            )}
            style={{ gridTemplateColumns: `auto repeat(${columnsCount}, minmax(min-content, 1fr))` }}
        >
            <div
                className={classNames(baseStyles.Heading, horizontalStyles.Heading, horizontalStyles['Heading--first'])}
            >
                {firstHeading}
            </div>
            {subjects.length > 0
                ? renderSubjects(subjects, gridColumnMapping, renderSubject, () => 1)
                : renderEmptyMessage(noSubjectsMessage)}
        </div>
    );
};
